import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, Minus, Plus, MessageCircle, ChevronDown } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import React, { useState } from 'react';
import { useCartStore } from '../hooks/useCartStore';
import { Branch } from '../types';
import { WhatsAppMessageService } from '../utils/whatsappMessageService';
import { BRANCHES } from '../config/siteConfig';
import { isBusinessHoursActive } from '../utils/businessHours';
import ShippingFormModal from './ShippingFormModal';

interface InquiryDrawerProps {
    currentBranch: Branch;
    onBranchChange: (branch: Branch) => void;
    onExplore?: () => void;
}

const InquiryDrawer: React.FC<InquiryDrawerProps> = ({ currentBranch, onBranchChange, onExplore }) => {
    const { items, removeItem, updateQuantity, clearCart, isDrawerOpen, setIsDrawerOpen } = useCartStore();
    const isAvailable = isBusinessHoursActive();
    const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);

    // Architect: Prevent background scroll when drawer is open
    React.useEffect(() => {
        if (isDrawerOpen) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isDrawerOpen]);

    return (
        <>
        <AnimatePresence>
            {isDrawerOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsDrawerOpen(false)}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]"
                    />

                    {/* Drawer */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-full max-w-md bg-zinc-950 border-l border-zinc-900 shadow-2xl z-[101] flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-zinc-900 flex items-center justify-between bg-zinc-950/50 backdrop-blur-md sticky top-0 z-10">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/10 p-2 rounded-lg">
                                    <ShoppingBag className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-white font-display font-bold uppercase tracking-tight">Lista de Consulta</h2>
                                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest">{items.length} {items.length === 1 ? 'pieza' : 'piezas'} seleccionadas</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsDrawerOpen(false)}
                                className="p-2 hover:bg-zinc-900 rounded-full transition-colors group"
                            >
                                <X className="h-6 w-6 text-zinc-500 group-hover:text-white" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-grow overflow-y-auto p-6 space-y-4">
                            {items.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-50">
                                    <div className="w-20 h-20 rounded-full border-2 border-dashed border-zinc-800 flex items-center justify-center">
                                        <ShoppingBag className="h-8 w-8 text-zinc-800" />
                                    </div>
                                    <p className="text-zinc-500 font-display uppercase text-xs tracking-widest">Tu lista está vacía</p>
                                    <button
                                        onClick={() => {
                                            setIsDrawerOpen(false);
                                            onExplore?.();
                                        }}
                                        className="text-white text-sm font-bold uppercase hover:scale-105 border border-white/20 px-6 py-2 rounded-full hover:bg-white hover:text-black transition-all"
                                    >
                                        Explorar Catálogo
                                    </button>
                                </div>
                            ) : (
                                items.map((item) => (
                                    <motion.div
                                        key={item.product.id}
                                        layout
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        className="group relative bg-zinc-900/50 border border-zinc-900 p-4 rounded-xl hover:border-zinc-800 transition-all"
                                    >
                                        <div className="flex gap-4">
                                            {/* Thumbnail */}
                                            <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border border-zinc-800">
                                                <img
                                                    src={item.product.image}
                                                    alt={item.product.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* Info */}
                                            <div className="flex-grow min-w-0">
                                                <div className="flex justify-between items-start mb-1">
                                                    <div>
                                                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">{item.product.make}</span>
                                                        <h3 className="text-white font-bold text-sm leading-tight line-clamp-2 pr-4">{item.product.name}</h3>
                                                    </div>
                                                    <button
                                                        onClick={() => removeItem(item.product.id)}
                                                        className="text-zinc-600 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>

                                                <div className="flex items-center justify-between mt-3">
                                                    {/* Quantity Controls */}
                                                    <div className="flex items-center bg-zinc-950 rounded-lg border border-zinc-800 p-1">
                                                        <button
                                                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                                                            className="p-1 text-zinc-500 hover:text-white transition-colors"
                                                        >
                                                            <Minus className="h-3 w-3" />
                                                        </button>
                                                        <span className="w-8 text-center text-white font-mono text-sm">{item.quantity}</span>
                                                        <button
                                                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                                                            className="p-1 text-zinc-500 hover:text-white transition-colors"
                                                        >
                                                            <Plus className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </div>

                        {/* Footer */}
                        {items.length > 0 && (
                            <div className="p-5 bg-zinc-950 border-t border-zinc-900 space-y-4">
                                <div className="flex items-center justify-between mb-4">
                                    <button
                                        onClick={clearCart}
                                        className="flex items-center gap-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest hover:text-red-500 transition-colors group"
                                    >
                                        <Trash2 className="h-3 w-3 text-zinc-600 group-hover:text-red-500 transition-colors" />
                                        Vaciar Lista
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em] text-center mb-2">Completar pedido vía WhatsApp</p>
                                    <button
                                        onClick={() => setIsShippingModalOpen(true)}
                                        className="w-full bg-white hover:bg-zinc-200 text-black font-display font-black uppercase tracking-widest py-4 rounded-xl flex items-center justify-center gap-2 transition-all shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]"
                                    >
                                        <MessageCircle className="h-5 w-5" />
                                        <span>Solicitar Envío</span>
                                    </button>
                                </div>

                                <p className="text-[10px] text-zinc-600 text-center leading-relaxed mt-4">
                                    Se enviará un mensaje consolidado para una cotización rápida y exacta.
                                </p>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
        
        <ShippingFormModal
            isOpen={isShippingModalOpen}
            onClose={() => setIsShippingModalOpen(false)}
            items={items}
            onSuccess={() => {
                clearCart();
                setIsDrawerOpen(false);
            }}
        />
        </>
    );
};

export default InquiryDrawer;
